import React, { useState, useEffect, Fragment } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { DatePicker, Popover, Select, Table } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import H3FormInput from '@Uilib/h3FormInput';
import imageDefault from '@Images/icons/imageDefault.png';
import H3Image from '@Uilib/h3Image';
import ProductFilterV2 from '@Components/Common/ProductFilterV2';
import SelectUOM from '@Components/Admin/Common/SelectUOM';
import './style.scss';
import Helpers from '@Apis/helpers';
import CFV2Actions from '@Actions/configurations/cfV2Actions';
import Constants, { QUANTITY, toISTDate } from '@Apis/constants';
import H3Text from '@Uilib/h3Text';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import QuickAddButton from '@Components/Common/UILib/QuickAddButton';
import CustomDocumentColumns from '@Components/Common/CustomDocumentColumns';
import ProductDescriptions from '../../../../Common/ProductDescriptions';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';
import PRZSelect from '../../../../Common/UI/PRZSelect';

const { Option } = Select;

const PurchaseIndentLines = ({
  handleProductChangeValue,
  handleProductChange,
  updateData,
  selectedTenant,
  selectedTenantDepartment,
  handleMultiProductChange,
  formSubmitted,
  customLineInputChange,
  data,
  handleDelete,
  cfPurchaseIndentLine,
  visibleColumns,
  setVisibleColumns,
  user,
  MONEY,
  getCartV2Loading,
  priceMasking,
  isCarryForward,
  selectedMO,
}) => {

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  const getColumns = () => {
    const columns = [
      {
        title: '',
        key: 'asset1',
        fixed: 'left',
        render: (text, record) => (
          <div style={{ width: '30px' }}>
            <H3Image src={record?.asset1 || imageDefault} className="pi-table__image" style={{ opacity: record?.asset1 ? 1 : 0.1 }} />
          </div>
        ),
        width: '60px',
        visible: true,
      },
      {
        title: 'Product',
        width: '250px',
        fixed: 'left',
        render: (text, record) => (
          <div className="pi-table__product-name">
            <ProductFilterV2
              record={record}
              productTypes={['STORABLE', 'SERVICE']}
              handleProductChangeValue={handleProductChangeValue}
              handleProductChange={handleProductChange}
              isPurchaseProduct
              required
              pendingFromVendor
              showError={formSubmitted && !record.product_sku_name}
              onClear={(key) => {
                const copyData = JSON.parse(JSON.stringify(data));
                const updatedData = copyData?.filter((item) => item.key !== key);
                if (updatedData?.length === 0) {
                  updateData([{
                    key: uuidv4(),
                    asset1: '',
                    product_sku_name: '',
                    uomGroup: null,
                    purchase_requisition_id: null,
                    product_sku_info: null,
                    product_sku_id: null,
                  }]);
                  return;
                } else {
                  updateData(updatedData);
                }
              }}
              disabled={record?.product_sku_id}
              // excludedProducts={data.map((item) => JSON.stringify(item?.tenant_product_id)) || []}
              selectedTenant={selectedTenant}
              tenantDepartmentId={selectedTenantDepartment}
              showClear
              enableAdvanceSearch
              handleMultiProductChange={handleMultiProductChange}
            />
            {record?.productCategoryInfo?.category_path?.length > 0 && (
              <ProductCategoryLabel
                categoryPath={record?.productCategoryInfo?.category_path}
                categoryName={record?.productCategoryInfo?.category_path?.at(
                  -1
                )}
                containerStyle={{
                  width: 'fit-content',
                }}
              />
            )}
            {
              !record?.remarkRequired && record.product_sku_name
              && (
                <QuickAddButton
                  label="Add Description"
                  onClick={() => {
                    const copyData = JSON.parse(JSON.stringify(data));
                    const newData = copyData.map((item) => {
                      if (item.key === record.key) {
                        return { ...item, remarkRequired: !record?.remarkRequired };
                      }
                      return item;
                    });
                    if (JSON.stringify(data) !== JSON.stringify(newData)) {
                      updateData(newData);
                    }
                  }}
                />
              )
            }
            {record?.remarkRequired ? (
              <ProductDescriptions
                descriptions={record?.remark}
                modules={{
                  toolbar: false,
                }}
                onChange={(e) => {
                  const copyData = JSON.parse(JSON.stringify(data));
                  const newData = copyData.map((item) => {
                    if (item.key === record.key) {
                      return { ...item, remark: e, remarkRequired: !!e?.replace(/<[^>]+>/g, '') };
                    }
                    return item;
                  });

                  if (JSON.stringify(data) !== JSON.stringify(newData)) {
                    updateData(newData);
                  }
                }}
                className="invoice-table__remarks"
                placeholder="Add description of this item.."
                key={record.key}
              />
            ) : ''}
          </div>
        ),
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'FG/SFG',
        render: (text, record) => {
          const parentProductInfo = record?.parent_mo_line_id ? selectedMO?.mo_lines?.find((item) => item.mo_line_id === record?.parent_mo_line_id)?.tenant_product_info : selectedMO?.mo_finished_goods?.find((item) => item.tenant_product_id === record?.fg_tenant_product_id)?.tenant_product_info;
          return (
            <div style={{ width: '140px' }}>
              {parentProductInfo ? (<React.Fragment>
                <Link to={`/inventory/product/view/${parentProductInfo?.product_sku_id}`} target='_blank'>
                  {`#${parentProductInfo?.internal_sku_code}`}
                </Link>
                <div>
                  {`${parentProductInfo?.ref_product_code ? `${parentProductInfo?.ref_product_code} - ` : ''}${parentProductInfo?.product_sku_name}`}
                </div>
                <div className="view-mo-rm-qty" style={{ backgroundColor: record?.parent_mo_line_id ? 'rgba(199,199,199,0.5)' : 'rgba(50,180,13,0.2)' }}>
                  {record?.parent_mo_line_id ? 'SFG' : 'FG'}
                </div>
              </React.Fragment>) : '-'}
            </div>
          );
        },
        visible: selectedMO && visibleColumns?.FG_SFG?.visible,
      },
      {
        title: 'Quantity',
        width: '120px',
        render: (text, record) => (
          <div style={{ width: '120px' }}>
            <H3FormInput
              value={record?.quantity}
              type="number"
              containerClassName={`orgInputContainer ${(formSubmitted && Number(record?.quantity) <= 0) ? 'create-pi__input' : ''}`}
              labelClassName="orgFormLabel"
              inputClassName="orgFormInput"
              onChange={(e) => {
                // e.preventDefault();
                CustomFieldHelpers.updateCustomColumnValue(Number(e.target.value || 0), record?.lineCustomFields?.find((i) => i?.fieldName === 'Quantity'), record?.lineCustomFields, (value) => customLineInputChange(value, record?.key));
              }}
            // disabled={}
            />
          </div>
        ),
        visible: visibleColumns?.QUANTITY?.visible,
      },
      {
        title: 'Unit',
        width: '120px',
        render: (text, record) => (
          <div style={{ width: '120px' }}>
            {record?.tenant_product_id ? (
              <div className="orgInputContainer">
                <PRZSelect
                  dropdownAlign={{ offset: [-6, 4] }}
                  filterOption={false}
                  className=""
                  value={record?.uomId}
                  onChange={(value) => {
                    const copyData = JSON.parse(JSON.stringify(data));
                    copyData.map((item) => {
                      if (item.key === record.key) {
                        item.uomId = value;
                        item.uom_info = item?.uom_list?.find((rec) => rec?.uom_id === value);
                      }
                      return item;
                    });
                    updateData(copyData);
                  }}
                >
                  {
                    record?.uom_list?.map((uom) => (
                      <Option key={uom?.uom_id} value={uom?.uom_id}>{`${uom?.uqc?.toProperCase()} (${uom?.uom_name?.toProperCase()})`}</Option>
                    ))
                  }
                </PRZSelect>
              </div>
            ) : (
              <div className="orgInputContainer">
                <SelectUOM
                  onChange={(value) => {
                    const copyData = JSON.parse(JSON.stringify(data));
                    copyData.map((item) => {
                      if (item.key === record.key) {
                        item.uom_id = value?.uom_id;
                        item.uom_info = value;
                      }
                      return item;
                    });
                    updateData(copyData);
                  }}
                  errorOutline={formSubmitted && !record?.uom_id}
                  selectedUom={record.uom_id}
                  hideTitle
                  uomGroup={record.uomGroup}
                  customStyle={{
                    width: '120px',
                  }}
                />
              </div>
            )}
          </div>
        ),
        visible: visibleColumns?.UNIT?.visible,
      },
      {
        title: 'Required by Date',
        width: '120px',
        render: (text, record) => (
          <div style={{ width: '120px' }}>
            <DatePicker
              value={record?.req_by_date ? toISTDate(record?.req_by_date) : ''}
              onChange={(value) => {
                const setEndOfDay = (date) => {
                  if (!date) return null;
                  const newDate = new Date(date);
                  newDate.setHours(23, 59, 59, 999);
                  newDate.setMinutes(newDate.getMinutes() - 330);
                  return newDate;
                };
                const endOfDayValue = setEndOfDay(value);
                const copyData = JSON.parse(JSON.stringify(data));
                copyData.map((item) => {
                  if (item.key === record.key) {
                    item.req_by_date = endOfDayValue;
                  }
                });
                updateData(copyData);
              }}
              style={{
                // borderColor: formSubmitted && !record?.req_by_date ? 'red' : 'rgba(68, 130, 218, 0.2)',
                borderColor: 'rgba(68, 130, 218, 0.2)',
                border: '1px solid rgba(68, 130, 218, 0.2)',
                borderRadius: '2px',
                height: '28px',
                padding: '1px 3px',
                width: '100%',
                background: 'white',
              }}
              format="DD/MM/YYYY"
            />
          </div>
        ),
        visible: visibleColumns?.REQ_BY_DATE?.visible,
      },
      {
        title: 'Planning Number',
        width: '120px',
        render: (text, record) => (
          <div style={{ width: '120px' }}>
            <H3FormInput
              value={record?.plan_number}
              type="text"
              containerClassName='orgInputContainer'
              labelClassName="orgFormLabel"
              inputClassName="orgFormInput"
              onChange={(e) => {
                const copyData = JSON.parse(JSON.stringify(data));
                copyData.map((item) => {
                  if (item.key === record.key) {
                    item.plan_number = e.target.value;
                  }
                });
                updateData(copyData);
              }}
            // disabled={}
            />
          </div>
        ),
        visible: visibleColumns?.PLAN_NUMBER?.visible,
      },
      {
        title: '',
        render: (text, record) => (
          <Fragment>
            {data.length !== record.key && (
              <div className="update-pi__delete-line-button" onClick={() => handleDelete(record.key)}>
                <CloseOutlined />
              </div>
            )}
          </Fragment>
        ),
        width: '40px',
        visible: true,
        fixed: 'right',
      },
    ];
    if (cfPurchaseIndentLine?.length) {
      columns.splice(6, 0, ...CustomFieldHelpers.renderCustomLineColumns(false, cfPurchaseIndentLine, visibleColumns, customLineInputChange, formSubmitted, '', '', '', '', '', '', '', isCarryForward));
    }

    return columns?.filter((item) => (item?.visible));
  };

  // const getData = (inputData) => (JSON.parse(JSON.stringify(inputData)));

  const getData = (inputData) => {
    const copyData = JSON.parse(JSON.stringify(inputData));

    const sortedData = Helpers.sortByParameter({
      data: copyData,
      parameter: 'internal_sku_code',
    });

    return [
      ...sortedData.filter((item) => item?.product_sku_id),
      ...sortedData.filter((item) => !item?.product_sku_id),
    ];
  };

  return (
    <Fragment>
      <Table
        bordered
        showHeader
        size="small"
        scroll={{ x: 'max-content' }}
        columns={getColumns()}
        dataSource={getData(data)}
        pagination={false}
        title={() => (
          <div className="pi-table__header">
            <H3Text
              text={`Purchase Indent Items (${data?.filter(
                (item) => item.product_sku_name?.length > 0,
              )?.length})`}
              className="form__input-row__label"
            />
            <div style={{ marginLeft: '5px' }}>
              <CustomDocumentColumns
                visibleColumns={visibleColumns || []}
                setVisibleColumns={(dt) => {
                  setVisibleColumns(dt);
                }}
                customColumns={cfPurchaseIndentLine || []}
                data={data}
                updateData={(updatedData) => updateData(updatedData)}
              />
            </div>
          </div>
        )}
        loading={getCartV2Loading}
      />
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, AddToCartReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getCartV2Loading: AddToCartReducers.getCartV2Loading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PurchaseIndentLines));